import { CheckIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { classNames } from '../../../helpers'

// merges a not yet committed input value into the tags,
// call this on submit so a typed but unconfirmed tag is not lost
export const flushTagInput = (tags, input) => {
  const pending = input.trim()
  if (!pending || tags.includes(pending)) return tags

  return [...tags, pending]
}

const TagsEditor = ({ tags, setTags, suggestions, input, setInput }) => {
  const { t } = useTranslation()

  const addTag = (tag) => {
    const trimmed = tag.trim()
    if (!trimmed) return

    if (!tags.includes(trimmed)) setTags([...tags, trimmed])
    setInput('')
  }

  const toggleTag = (tag) => {
    if (tags.includes(tag)) setTags(tags.filter((item) => item !== tag))
    else setTags([...tags, tag])
  }

  return (
    <div>
      <span className='block text-sm font-medium text-gray-700'>
        {t('settings.user_management.tags_label')}
      </span>
      <p className='mt-0.5 text-xs text-gray-400'>
        {t('settings.user_management.tags_description')}
      </p>
      {tags.length > 0 && (
        <div className='mt-2.5 flex flex-wrap gap-1.5'>
          {tags.map((tag) => (
            <span
              key={tag}
              className='inline-flex items-center gap-x-1.5 rounded-full bg-paleWhiteBlue py-1 pl-2.5 pr-1.5 text-xs font-semibold text-darkBlue'
            >
              {tag}
              <button
                type='button'
                onClick={() => toggleTag(tag)}
                className='inline-flex text-darkBlue hover:text-primaryBlue'
              >
                <XMarkIcon className='h-3.5 w-3.5' aria-hidden='true' />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className='mt-2.5 flex gap-x-2'>
        <input
          type='text'
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              addTag(input)
            }
          }}
          placeholder={t('settings.user_management.tags_input_placeholder')}
          className='block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-sky-500 focus:ring-sky-500'
        />
        <button
          type='button'
          onClick={() => addTag(input)}
          className='whitespace-nowrap rounded-md border border-gray-300 bg-white px-3.5 text-sm font-medium text-primaryBlue shadow-sm hover:bg-gray-50'
        >
          {t('settings.user_management.tags_add_action')}
        </button>
      </div>
      {suggestions.length > 0 && (
        <div className='mt-3'>
          <span className='block text-[11px] font-semibold uppercase tracking-wide text-gray-400'>
            {t('settings.user_management.tags_suggestions_label')}
          </span>
          <div className='mt-1.5 flex flex-wrap gap-1.5'>
            {suggestions.map((tag) => {
              const selected = tags.includes(tag)

              return (
                <button
                  key={tag}
                  type='button'
                  onClick={() => toggleTag(tag)}
                  className={classNames(
                    'inline-flex items-center gap-x-1 rounded-full px-3 py-1 text-xs font-semibold transition-colors',
                    selected
                      ? 'bg-primaryBlue text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  )}
                >
                  {selected ? (
                    <CheckIcon className='h-3 w-3' aria-hidden='true' />
                  ) : (
                    <PlusIcon className='h-3 w-3' aria-hidden='true' />
                  )}
                  {tag}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

TagsEditor.propTypes = {
  tags: PropTypes.array.isRequired,
  setTags: PropTypes.func.isRequired,
  suggestions: PropTypes.array,
  input: PropTypes.string.isRequired,
  setInput: PropTypes.func.isRequired
}

TagsEditor.defaultProps = {
  suggestions: []
}

export default TagsEditor
